import { useEffect, useRef } from "react";

import { createLogger } from "@/logger";
import { downloadFeedItemContent } from "@/models/actions/initialFeedLoad";
import type { PreferredLanguage } from "@/models/languages";
import { store } from "@/store/store";

const log = createLogger("feed");

/**
 * `${id}:${language}` keys a download is fetching right now, from any caller, so two hooks that list the same message
 * in one commit ask for it once.
 */
const inFlight = new Set<string>();

/**
 * Downloads the text of each message in `idsKey` whose content the store has not loaded, once per message and
 * language.
 *
 * The warmup loads items without their content, and a language change clears every item's loaded content, so a
 * Dispatch surface that shows a message's text downloads it. `${id}:${language}` keys already requested this mount, so
 * a recompute while a download is in flight asks for nothing twice, and the first visit to a language asks again; what
 * an earlier visit downloaded stays in the store. The effect keys on the joined ids, so the reset that clears loaded
 * content before the language changes does not fetch in the old language. A failed download forgets its keys, so the
 * next change retries.
 *
 * @param workspaceId - The workspace the messages are in.
 * @param idsKey - The message ids, comma-joined; empty for none.
 * @param language - The member's language.
 * @param options.what - What the messages are, for the failure log ("Driver message").
 * @param options.holds - Whether the store already holds what the caller shows for a message, which skips it.
 */
export function useMessageContentDownload(
  workspaceId: string,
  idsKey: string,
  language: string,
  { what, holds }: { what: string; holds?: (id: string, language: string) => boolean },
): void {
  const requested = useRef(new Set<string>());
  const holdsRef = useRef(holds);
  holdsRef.current = holds;
  useEffect(() => {
    if (idsKey === "" || !workspaceId) return;
    const missing = idsKey.split(",").filter((id) => {
      const key = `${id}:${language}`;
      if (requested.current.has(key) || inFlight.has(key)) return false;
      return store.get("item", id)?.loadedContent !== true && !holdsRef.current?.(id, language);
    });
    if (missing.length === 0) return;
    const keys = missing.map((id) => `${id}:${language}`);
    for (const key of keys) {
      requested.current.add(key);
      inFlight.add(key);
    }
    downloadFeedItemContent(workspaceId, missing, language as PreferredLanguage)
      .catch((error: unknown) => {
        for (const key of keys) requested.current.delete(key);
        log.warn(`[DISPATCH] ${what} content download failed; the next change retries`, { error });
      })
      .finally(() => {
        for (const key of keys) inFlight.delete(key);
      });
  }, [idsKey, workspaceId, language, what]);
}
